import { motion } from "framer-motion";
import React from "react";
import { Link, useParams } from "react-router-dom";

import { Button } from "@/app/components/ui/button.js";
import { Input } from "@/app/Views/JobsPage/components/Input.jsx";

import { useCompany, useCompanyJobs } from "../../services/create-resume/queries";

function ClaimCompany() {
	const { id } = useParams();
	const [isCodeSent, setIsCodeSent] = React.useState(false);
	const { data: jobs, isLoading: isLoadingJobs } = useCompanyJobs(id);
	const { data: companyData, isLoading: loadingCompany, isError } = useCompany(id);

	// Loading skeleton for company header
	const CompanyHeaderSkeleton = () => (
		<motion.div
			className="flex justify-center flex-col gap-4 items-center animate-pulse"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3 }}
		>
			<div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100 bg-gray-200" />
			<div className="ml-6 flex flex-col items-center">
				<div className="h-8 bg-gray-200 rounded w-48 mb-2" />
			</div>
		</motion.div>
	);

	// Loading skeleton for jobs section
	const JobsSkeleton = () => (
		<motion.div
			className="mt-4 animate-pulse"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3, delay: 0.1 }}
		>
			<div className="pt-6">
				<div className="h-8 bg-gray-200 rounded w-64 mb-4" />
				<div className="space-y-4">
					{[...Array(3)].map((_, index) => (
						<motion.div
							key={index}
							className="border border-gray-200 rounded-lg p-4 bg-white"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.3, delay: index * 0.1 }}
						>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
									<div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
									<div className="flex flex-wrap gap-2">
										<div className="h-6 bg-gray-200 rounded-full w-20" />
										<div className="h-6 bg-gray-200 rounded-full w-24" />
										<div className="h-6 bg-gray-200 rounded-full w-16" />
									</div>
								</div>
								<div className="ml-4">
									<div className="h-10 bg-gray-200 rounded w-20" />
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</div>
		</motion.div>
	);

	if (loadingCompany || isLoadingJobs) {
		return (
			<section className="max-w-4xl mx-auto my-12 px-4">
				<CompanyHeaderSkeleton />
				<JobsSkeleton />

				<footer className="mt-12 pt-8 border-t border-gray-200 text-center">
					<p className="text-sm text-gray-500 font-inter">
						Powered by{" "}
						<a
							href="https://dproz.com"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-700 hover:text-primary-800 font-medium hover:underline"
						>
							Dproz.com
						</a>
					</p>
				</footer>
			</section>
		);
	}

	if (isError) {
		return (
			<section className="max-w-4xl mx-auto my-12 px-4">
				<motion.div
					className="text-center py-12"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5 }}
				>
					<p className="text-gray-500 text-lg">Company not found</p>
				</motion.div>

				<motion.footer
					className="mt-12 pt-8 border-t border-gray-200 text-center"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, delay: 0.2 }}
				>
					<p className="text-sm text-gray-500 font-inter">
						Powered by{" "}
						<a
							href="https://dproz.com"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-700 hover:text-primary-800 font-medium hover:underline"
						>
							Dproz.com
						</a>
					</p>
				</motion.footer>
			</section>
		);
	}

	// Empty state when no jobs are available
	if (!loadingCompany && !isLoadingJobs && companyData && jobs && jobs?.data?.length === 0) {
		return (
			<motion.section
				className="max-w-4xl mx-auto my-12 px-4"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.5 }}
			>
				<motion.div
					className="flex justify-center flex-col gap-4 items-center mb-8"
					initial={{ opacity: 0, y: -20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.2 }}
				>
					<div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
						<img
							alt={companyData?.data?.company_name + " logo"}
							loading="lazy"
							width="96"
							height="96"
							decoding="async"
							className="w-full h-full object-cover"
							src={companyData?.data?.logo_url}
							onError={(e) => (e.target.src = "/images/co-placeholder.png")}
						/>
					</div>
					<div>
						<h2 className="!text-3xl font-bold text-gray-900 font-plus-jakarta text-center">
							{companyData?.data?.company_name}
						</h2>
					</div>
				</motion.div>

				<motion.div
					className="flex flex-col items-center justify-center py-12"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
				>
					<div className="mb-6">
						<img src="/images/empty-resumes.svg" alt="No jobs available" className="w-32 h-32 opacity-60" />
					</div>
					<h3 className="text-2xl font-semibold text-gray-800 mb-3 font-plus-jakarta">No Open Positions Available</h3>
					<p className="text-gray-500 text-center max-w-md">
						{companyData?.data?.company_name} doesn&apos;t have any job openings at the moment. Check back later for new
						opportunities!
					</p>
				</motion.div>

				<motion.footer
					className="mt-12 pt-8 border-t border-gray-200 text-center"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.5, delay: 0.6 }}
				>
					<p className="text-sm text-gray-500 font-inter">
						Powered by{" "}
						<a
							href="https://dproz.com"
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary-700 hover:text-primary-800 font-medium hover:underline"
						>
							Dproz.com
						</a>
					</p>
				</motion.footer>
			</motion.section>
		);
	}

	return (
		<motion.section
			className="max-w-4xl mx-auto my-12 px-4"
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.5 }}
		>
			<motion.div
				className="flex justify-center flex-col gap-4 items-center"
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, delay: 0.2 }}
			>
				<div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
					<img
						alt={companyData?.data?.company_name + " logo"}
						loading="lazy"
						width="96"
						height="96"
						decoding="async"
						className="w-full h-full object-cover"
						src={companyData?.data?.logo_url}
						onError={(e) => (e.target.src = "/images/co-placeholder.png")}
					/>
				</div>
				<div>
					<h2 className="!text-3xl font-bold text-gray-900 font-plus-jakarta text-center">
						{companyData?.data?.company_name}
					</h2>
				</div>
			</motion.div>
			{isCodeSent ? (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
				>
					<h2 className="text-2xl font-semibold mb-4 mt-6 font-plus-jakarta">Confirm OTP</h2>
					<p>Enter the OTP sent to your email.</p>
					<div className="mt-6">
						<Input
							placeholder="Search applications..."
							className="pl-9 h-9 bg-gray-50 border-light-blue text-gray-900 placeholder:text-gray-600"
						/>
						<div className="flex justify-between items-center">
							<Link to="/employer/dashboard/overview">
								<Button className="mt-6">Submit</Button>
							</Link>
							<p>
								Did not get the code? <span className="cursor-pointer text-blue underline">Resend!</span>
							</p>
						</div>
					</div>
				</motion.div>
			) : (
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.4 }}
				>
					<h2 className="text-2xl font-semibold mb-4 mt-6 font-plus-jakarta">Is This Your Company?</h2>
					<p>We created this profile using public available information. Verify your work email to manage it.</p>
					<div className="mt-6">
						<Input
							placeholder="Search applications..."
							className="pl-9 h-9 bg-gray-50 border-light-blue text-gray-900 placeholder:text-gray-600"
						/>
						<Button className="mt-6" onClick={() => setIsCodeSent(true)}>
							Claim this page
						</Button>
					</div>
				</motion.div>
			)}

			<motion.footer
				className="mt-12 pt-8 border-t border-gray-200 text-center"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.5, delay: 0.6 }}
			>
				<p className="text-sm text-gray-500 font-inter">Only company email addresses are accepted.</p>
			</motion.footer>
		</motion.section>
	);
}

export default ClaimCompany;
